import { useCallback, useEffect, useRef } from 'react';

import { nanoid } from 'nanoid';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

import {
  trackOpsPilot,
  type CloseTrigger,
  type QuerySource,
} from './_analytics';
import { useChatbotState } from './useChatbotState';

type VisitState = {
  openId: string; // internal — sent to PostHog as open_id
  questionsAsked: number;
  didType: boolean;
};

type UseChatVisitTrackingProps = {
  getContextId: () => string;
  handleCloseChatbot: () => void;
  sendMessage: (
    text: string,
    chatId?: string,
    source?: QuerySource
  ) => void | Promise<void> | Promise<boolean>;
  focusTextarea: () => void;
  currentChatIdRef: React.MutableRefObject<string | undefined>;
};

export const useChatWithTracking = ({
  getContextId,
  handleCloseChatbot,
  sendMessage,
  focusTextarea,
  currentChatIdRef,
}: UseChatVisitTrackingProps) => {
  const { isOpen, initialMessage, inputValue, setInputValue, hadUnreadOnOpen } =
    useChatbotState();

  const { currentOrganization } = useOrganizations();
  const organizationId = currentOrganization?.id;
  const visitRef = useRef<VisitState | null>(null);
  const closeTriggerRef = useRef<CloseTrigger>('navigation');

  // Ref so handleSendMessage always reads the latest value without being
  // re-created on every keystroke (avoids unnecessary closure churn).
  const inputValueRef = useRef(inputValue);

  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  // Counts a question for the current visit. No-op if the panel is closed or
  // the text is empty — safe to call unconditionally at any question entry point.
  const countQuestion = useCallback((text: string) => {
    if (visitRef.current && text.trim()) {
      visitRef.current.questionsAsked += 1;
    }
  }, []);

  // Guarded by `if (!visit)` so it is safe to call multiple times: when
  // `isOpen` flips false the effect body calls it, then the previous render's
  // cleanup also fires — only the first call emits, the second is a no-op.
  const emitVisitClose = useCallback(() => {
    const visit = visitRef.current;
    if (!visit) return;
    trackOpsPilot.chatClosed({
      open_id: visit.openId,
      context_id: getContextId(),
      organization_id: organizationId,
      trigger: closeTriggerRef.current,
      questions_asked: visit.questionsAsked,
      did_type: visit.didType,
    });
    visitRef.current = null;
    closeTriggerRef.current = 'navigation';
  }, [getContextId, organizationId]);

  useEffect(() => {
    if (isOpen) {
      visitRef.current = {
        openId: nanoid(),
        questionsAsked: 0,
        didType: false,
      };
      trackOpsPilot.chatOpened({
        open_id: visitRef.current.openId,
        context_id: getContextId(),
        organization_id: organizationId,
        trigger: initialMessage ? 'deep-link' : 'launcher',
        had_unread: hadUnreadOnOpen,
      });
    } else {
      emitVisitClose();
    }
    return () => emitVisitClose();
    // Intentionally depends only on `isOpen`. All other values referenced inside
    // (getContextId, organizationId, initialMessage, hadUnreadOnOpen) are either
    // stable callbacks/refs or are only meaningful at the moment the panel opens —
    // including them would fire spurious open/close pairs on unrelated re-renders.
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      const visit = visitRef.current;
      if (visit && !visit.didType && value.trim().length > 0) {
        visit.didType = true;
        trackOpsPilot.chatInputStarted({
          open_id: visit.openId,
          context_id: getContextId(),
          organization_id: organizationId,
        });
      }
    },
    [setInputValue, getContextId, organizationId]
  );

  const handleSendMessage = useCallback(async () => {
    const text = inputValueRef.current;
    setInputValue('');
    countQuestion(text);
    await sendMessage(text, currentChatIdRef.current);
    focusTextarea();
  }, [
    setInputValue,
    countQuestion,
    sendMessage,
    focusTextarea,
    currentChatIdRef,
  ]);

  const handleSuggestionClick = useCallback(
    (text: string) => {
      countQuestion(text);
      return sendMessage(text, currentChatIdRef.current, 'suggestion');
    },
    [countQuestion, sendMessage, currentChatIdRef]
  );

  const closeViaButton = useCallback(() => {
    closeTriggerRef.current = 'close-button';
    handleCloseChatbot();
  }, [handleCloseChatbot]);

  return {
    handleInputChange,
    handleSendMessage,
    handleSuggestionClick,
    closeViaButton,
    countQuestion,
  };
};
